import { Box, Button, ButtonProps } from "@outschool/backpack";
import { TFunction, useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import * as Time from "@outschool/time";
import { DocumentNode } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import _ from "lodash";
import React, { useState } from "react";

import * as Enrollment from "../../../shared/Enrollment";
import { isCharterOrder, isESAOrder } from "../../../shared/EnrollmentPayment";
import {
  EnrolledClassDetailsQuery,
  EnrolledClassDetailsQueryVariables
} from "../../../shared/generated/graphql";
import * as User from "../../../shared/User";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import { ResumeOngoingEnrollment } from "../enrolling/ResumeOngoingEnrollment";
// @ts-ignore - JSX import
import MessageModal from "../MessageModal";
import {
  EnrolledClassActivity,
  EnrolledClassEnrollment,
  EnrolledClassGiftEnrollment,
  EnrolledClassSection
} from "./EnrolledClassTypes";
import ParentPauseOngoingButton from "./ParentPauseOngoingButton";
import ParentTransferButton from "./ParentTransferButton";
import ParentWithdrawButton from "./ParentWithdrawButton";

const studentSummary = (t: TFunction, enrollment: $TSFixMe) => {
  if (!enrollment.parent || !enrollment.learner) {
    return t`Unclaimed Gift Enrollment`;
  }

  const studentName = Enrollment.displayName(enrollment);
  const studentAge = Enrollment.displayAge(enrollment);

  // handle case where enrollment has ended but was not paused
  const paymentScheduleEndTime = enrollment.paymentSchedule?.endTime;
  if (Enrollment.isOngoingStopped(enrollment) || paymentScheduleEndTime) {
    const enrollmentEndTime = dayjs(
      Enrollment.isOngoingStopped(enrollment)
        ? enrollment.ongoingEndTime
        : paymentScheduleEndTime
    );
    const enrollmentHasEnded =
      enrollmentEndTime.isBefore() && !enrollment.pauseWindow;
    if (enrollmentHasEnded) {
      return t("{{studentName}} ({{studentAge}}) was enrolled.", {
        studentName,
        studentAge
      });
    }
  }

  return t("{{studentName}} ({{studentAge}}) is enrolled.", {
    studentName,
    studentAge
  });
};

interface EnrollmentDescription {
  strikethroughText?: string;
  normalText?: string;
}

/**
 * The text description for each enrollment line. If the strikethroughText
 * field is defined, that text should be displayed with a strikethrough style.
 * normalText should be displayed without the strikethrough, and both can be
 * defined at once.
 */
export const enrollmentDescription = (
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment,
  timeZone: string,
  t: TFunction
): EnrollmentDescription => {
  const isGiftEnrollment = enrollment.__typename === "GiftEnrollment";
  const isClaimedGiftEnrollment =
    isGiftEnrollment &&
    enrollment.giftClaimedAt &&
    enrollment.currentUserIsPurchaser;

  if (isClaimedGiftEnrollment) {
    if (Enrollment.isOngoing(enrollment)) {
      return {
        normalText: t("Claimed Gift Enrollment ({{giftIdNumber}})", {
          giftIdNumber: Enrollment.orderSlugId(enrollment)
        })
      };
    } else {
      return { normalText: t("Claimed Gift Enrollment") };
    }
  }

  if (enrollment.pauseWindow) {
    const pauseStartDate = dayjs(enrollment.pauseWindow.startDate).utc(true);

    const pauseEndDate = dayjs(enrollment.pauseWindow.endDate).utc(true);
    const isPauseUpcoming = pauseStartDate.isAfter();

    if (isPauseUpcoming) {
      return {
        normalText:
          studentSummary(t, enrollment) +
          " " +
          t("Pause upcoming: {{pauseStartDate}} to {{pauseEndDate}}", {
            pauseStartDate: Time.formatDate(pauseStartDate),
            pauseEndDate: Time.formatDate(pauseEndDate)
          })
      };
    }

    if (dayjs().isBetween(pauseStartDate, pauseEndDate, "millisecond", "[)")) {
      return {
        strikethroughText: studentSummary(t, enrollment),
        normalText: t("Enrollment paused (ending {{pauseEndDate}})", {
          pauseEndDate: Time.formatDate(pauseEndDate)
        })
      };
    }

    OsPlatform.captureError(
      new Error("Enrollment pause window is out of date")
    );
    return {
      normalText: studentSummary(t, enrollment)
    };
  }

  if (Enrollment.isWithdrawn(enrollment)) {
    return {
      strikethroughText: studentSummary(t, enrollment),
      normalText: t("({{orderNumber}}) (Withdrawn)", {
        orderNumber: Enrollment.orderSlugId(enrollment)
      })
    };
  }

  const paymentScheduleEndTime = enrollment.paymentSchedule?.endTime;
  if (Enrollment.isOngoingStopped(enrollment) || paymentScheduleEndTime) {
    const enrollmentEndTime = dayjs(
      Enrollment.isOngoingStopped(enrollment)
        ? enrollment.ongoingEndTime
        : paymentScheduleEndTime
    );
    const enrollmentHasEnded =
      enrollmentEndTime.isBefore() && !enrollment.pauseWindow;
    const enrollmentEndDate = Time.formatDate(enrollmentEndTime, timeZone);
    if (enrollmentHasEnded) {
      return {
        normalText:
          studentSummary(t, enrollment) +
          " " +
          t("(subscription ended on {{enrollmentEndDate}})", {
            enrollmentEndDate
          })
      };
    } else {
      return {
        normalText:
          studentSummary(t, enrollment) +
          " " +
          t("(subscription ends on {{enrollmentEndDate}})", {
            enrollmentEndDate
          })
      };
    }
  }

  if (isGiftEnrollment && enrollment.parent) {
    if (Enrollment.isOngoing(enrollment)) {
      return {
        normalText: studentSummary(t, enrollment) + " " + t("Gift Enrollment")
      };
    } else {
      return {
        normalText:
          studentSummary(t, enrollment) +
          " " +
          t("Gift Enrollment ({{orderNumber}})", {
            orderNumber: Enrollment.orderSlugId(enrollment)
          })
      };
    }
  }

  return { normalText: studentSummary(t, enrollment) };
};

interface EnrollmentControlsProps {
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment;
  activity: EnrolledClassActivity;
  rootQuery?: {
    query: DocumentNode;
    variables: EnrolledClassDetailsQueryVariables;
  };
  section: EnrolledClassSection;
  /** Props given to each Button */
  buttonProps?: ButtonProps;
}

//todo: move this and getControlsToDisplay into GQL
function enrollmentCanBeResumed(
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment,
  activity: EnrolledClassActivity
): boolean {
  const enrollmentEndTime = dayjs(
    activity.isClub
      ? enrollment.paymentSchedule?.endTime
      : enrollment.ongoingEndTime
  );
  //Enrollment has not ended if there is a pause window on it
  const enrollmentHasEnded =
    !!enrollment.paymentSchedule?.endTime &&
    enrollmentEndTime.isBefore() &&
    !enrollment.pauseWindow;
  // has this week's payment been refunded?
  const startOfThisWeek = Time.startOfIsoWeekUtc(dayjs());
  const endOfThisWeek = Time.endOfIsoWeekUtc(dayjs());
  const lastEnrollmentPayment = _.orderBy(
    enrollment.payments,
    "paid_at",
    "desc"
  )[0];
  // was this payment made this week AND refunded?
  const thisWeekRefunded =
    lastEnrollmentPayment &&
    lastEnrollmentPayment.paid_at <= endOfThisWeek &&
    lastEnrollmentPayment.paid_at >= startOfThisWeek &&
    lastEnrollmentPayment.unrefundedCents === 0;
  return !enrollmentHasEnded && !activity.isClub && !thisWeekRefunded;
}

export enum EnrollmentControl {
  Resume,
  Pause,
  RequestTransfer,
  WithdrawOrStop,
  Help
}

/*
 * Returns a sorted list of controls to display for an enrollment.
 * Each control has its own if statement so they can indepdently
 * be decided.
 */

export function getControlsToDisplay(enrollment: $TSFixMe, activity: $TSFixMe) {
  const controls: EnrollmentControl[] = [];

  const isClaimedGiftEnrollment =
    enrollment.__typename === "GiftEnrollment" &&
    enrollment.giftClaimedAt &&
    enrollment.currentUserIsPurchaser;
  const isEsaPurchase = enrollment.payments.some(isESAOrder);
  const isCharterPurchase = enrollment.payments.some(isCharterOrder);
  const stoppedOrPaymentNeeded =
    Enrollment.isOngoingStopped(enrollment) ||
    Boolean(enrollment.paymentSchedule?.stoppedAt);

  //There are 3 special cases that pre-empt the normal logic, so they
  //are combined into a condition which disables all other controls.
  const noControls =
    Enrollment.isWithdrawn(enrollment) ||
    isClaimedGiftEnrollment ||
    stoppedOrPaymentNeeded;

  //Claimed Gift Enrollments only have Help, because they are
  //being used by the giftee, and this is the gifting user
  //Help will be removed fromt this function soon, as it will be moved into the sidebar
  if (!noControls || isClaimedGiftEnrollment) {
    controls.push(EnrollmentControl.Help);
  }

  //The only control stoppedOrPaymentNeeded has is Resume
  if (
    !(Enrollment.isWithdrawn(enrollment) || isClaimedGiftEnrollment) &&
    !isEsaPurchase &&
    !isCharterPurchase &&
    stoppedOrPaymentNeeded &&
    enrollmentCanBeResumed(enrollment, activity)
  ) {
    controls.push(EnrollmentControl.Resume);
  }

  if (!noControls) {
    if (!activity.isClub && enrollment.canBePaused) {
      controls.push(EnrollmentControl.Pause);
    }

    if (enrollment.canBeWithdrawn || enrollment.canBeStopped) {
      controls.push(EnrollmentControl.WithdrawOrStop);
    }

    if (
      (enrollment.canBeWithdrawn || enrollment.canBeStopped) &&
      !activity.isClub &&
      !activity.isSelfPaced
    ) {
      controls.push(EnrollmentControl.RequestTransfer);
    }
  }
  return controls.sort();
}

/**
 * The controls present on each EnrollmentLine.
 */
export const EnrollmentControls = ({
  enrollment,
  activity,
  section,
  rootQuery,
  buttonProps
}: EnrollmentControlsProps) => {
  const controlsToDisplay = getControlsToDisplay(enrollment, activity);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const redirectToPause = () => {
    setWithdrawOpen(false);
    setPauseOpen(true);
  };
  function displaySingleControl(control: EnrollmentControl) {
    switch (control) {
      case EnrollmentControl.Help:
        return <HelpButton activity={activity} {...buttonProps} />;
      case EnrollmentControl.Resume:
        return (
          <ResumeOngoingEnrollment enrollment={enrollment} {...buttonProps} />
        );
      case EnrollmentControl.WithdrawOrStop:
        return (
          <ParentWithdrawButton
            activity={activity}
            rootQuery={rootQuery}
            section={section}
            enrollment={enrollment}
            open={withdrawOpen}
            setOpen={setWithdrawOpen}
            redirectToPause={redirectToPause}
            {...buttonProps}
          />
        );
      case EnrollmentControl.Pause:
        return (
          <ParentPauseOngoingButton
            activity={activity}
            section={section}
            enrollment={enrollment}
            open={pauseOpen}
            setOpen={setPauseOpen}
            {...buttonProps}
          />
        );
      case EnrollmentControl.RequestTransfer:
        return (
          <ParentTransferButton
            activity={activity}
            section={section}
            enrollment={enrollment}
            {...buttonProps}
          />
        );
      default:
        // for exhaustive check at compile time
        ((c: never) => {
          throw new Error(`Unhandled control type: ${(c as any).type}`);
        })(control);
    }
  }

  return (
    <Box
      sx={{
        "& > :not(:last-child) ": {
          borderRight: "1px solid",
          // Left align Buttons to left of text
          marginLeft: -14,
          // match ButtonGroup styling
          lineHeight: 1,
          borderColor: "rgba(129, 136, 248, 0.5)",
          borderWidth: "2px",
          padding: "0.6rem 1.4rem",
          marginRight: 14,
          marginBottom: 8
        }
      }}
    >
      {controlsToDisplay.map((e, idx) => (
        <Box sx={{ display: "inline-block" }} key={idx}>
          {displaySingleControl(e)}
        </Box>
      ))}
    </Box>
  );
};

type EnrollmentProps = {
  section: EnrolledClassSection;
  activity: EnrolledClassActivity;
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment;
  rootQuery: {
    query: DocumentNode;
    variables: EnrolledClassDetailsQueryVariables;
  };
};
export const EnrollmentLine = ({
  enrollment,
  activity,
  section,
  rootQuery
}: EnrollmentProps) => {
  const { t } = useTranslation("client\\components\\enrolled\\YourLearners");
  const { timeZone } = useTimeZone();

  const { normalText, strikethroughText } = enrollmentDescription(
    enrollment,
    timeZone,
    t
  );
  return (
    <Box
      flex
      sx={{
        justifyContent: "space-between",
        padding: "1em",
        marginTop: "0.5em",
        width: "100%",
        borderBottom: "1px solid",
        borderColor: "#f1f2f3"
      }}
    >
      <Box sx={{ marginRight: "1em" }} data-private>
        {strikethroughText && <s>{strikethroughText}</s>} {normalText}
      </Box>
      <Box>
        <EnrollmentControls
          activity={activity}
          enrollment={enrollment}
          rootQuery={rootQuery}
          section={section}
        />
      </Box>
    </Box>
  );
};

interface HelpButtonProps extends ButtonProps {
  activity: NonNullable<EnrolledClassDetailsQuery["section"]>["activity"];
}

function HelpButton({ activity, onClick, ...buttonProps }: HelpButtonProps) {
  const { t } = useTranslation("client\\components\\enrolled\\YourLearners");
  const { currentUser } = useSession();
  return (
    <MessageModal
      renderButton={(open: $TSFixMe) => (
        <Button
          variant="link"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            open(e);
            onClick?.(e);
          }}
          {...buttonProps}
        >
          {t`Help`}
        </Button>
      )}
      user={currentUser}
      heading={`Message to ${User.shortenedLeaderName(activity.leader.name)}`}
      subject={t("Help needed for: {{classTitle}}", {
        classTitle: activity.title
      })}
      context={{
        action: "Enrolled parent to teacher",
        activityTitle: activity.title,
        subject: "Message about help for: " + activity.title
      }}
      recipients={[activity.leader]}
    />
  );
}
